import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';

import { api } from '@/lib/api';
import { useLookAlong } from '@/lib/impersonation';
import { legSchermVast } from '@/lib/melden/capture';
import { MeldPaneel, type Opname } from './MeldPaneel';

/**
 * "Meld iets" (klant-feedback-lus). Visible only in a person's own session and
 * only when Qontrol says this customer may report (a test party, or the app
 * released) and the brake MELDKNOP_AAN_VOOR is not "uit" -- the backend answers
 * that in one question. A look-along never asks.
 */
export function MeldKnop() {
  const { t } = useTranslation();
  const lookAlong = useLookAlong();
  const vrijgave = useQuery({
    queryKey: ['meldingen', 'vrijgave'],
    queryFn: () => api.meldingen.vrijgave(),
    enabled: !lookAlong,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const [opname, setOpname] = useState<Opname | null>(null);
  const [bezig, setBezig] = useState(false);

  if (lookAlong || !vrijgave.data?.aan) return null;

  async function open() {
    setBezig(true);
    try {
      // Both pictures now, before the dialog exists: once it is open, its overlay is in every new picture.
      // ponytail: two captures even when "Gegevens tonen" is never switched on.
      const gemaskeerd = await legSchermVast(false);
      const leesbaar = await legSchermVast(true);
      setOpname({ gemaskeerd, leesbaar });
    } finally {
      setBezig(false);
    }
  }

  return (
    <>
      <button
        type="button"
        data-capture="exclude"
        disabled={bezig}
        onClick={() => void open()}
        className="fixed bottom-4 right-4 z-40 flex h-10 items-center gap-2 rounded-full bg-accent px-4 text-[13px] font-medium text-on-accent shadow-lg hover:bg-accent/90 disabled:opacity-60"
      >
        <MessageSquare className="h-4 w-4" aria-hidden />
        {t('melden.knop')}
      </button>
      {opname && <MeldPaneel opname={opname} onSluit={() => setOpname(null)} />}
    </>
  );
}
